import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { logError } from "@/lib/log";
import { startCheckout } from "@/lib/checkout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Server, MapPin, ShieldCheck, Copy, Download, RefreshCw } from "lucide-react";
import { CouponField } from "./CouponField";

const copyProxies = (proxies) => {
  navigator.clipboard.writeText(proxies.join("\n"));
  toast.success(`Copied ${proxies.length} ISP proxies`);
};

const downloadProxies = (order) => {
  const blob = new Blob([order.proxies.join("\n")], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${order.order_id}.txt`;
  a.click();
  URL.revokeObjectURL(url);
};

const statusStyle = (status) => {
  if (status === "cancelled") return "text-zinc-400 border-zinc-300";
  if (status === "delivered") return "text-neon border-neon/30";
  return "text-amber-600 border-amber-300";
};

export function ShopISP({ ispOrders }) {
  const [qty, setQty] = useState(25);
  const [coupon, setCoupon] = useState(null);
  const [avail, setAvail] = useState(null);

  useEffect(() => {
    api.get("/isp/availability").then(({ data }) => setAvail(data)).catch(logError);
  }, []);

  const available = avail?.available ?? null;
  const soldOut = available !== null && available < 25;
  const base = qty * 2;
  const discount = coupon ? (coupon.percent_off ? base * coupon.percent_off / 100 : Math.min(coupon.amount_off || 0, base)) : 0;
  const final = Math.max(base - discount, 0);

  const buy = () => {
    if (qty < 25) { toast.error("Minimum 25 ISPs"); return; }
    if (available !== null && qty > available) { toast.error(`Only ${available} ISPs available`); return; }
    startCheckout({ product: "isp_ip", quantity: qty, promotion_code: coupon?.code });
  };

  return (
    <div className="space-y-6">
      <div className="led-border bg-white border border-[#E4EEEC] rounded-3xl p-6 shadow-sm">
        <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
          <div className="flex items-center gap-2"><Server className="w-5 h-5 text-neon" aria-hidden="true" /><h3 className="font-head text-xl font-semibold text-ink">FRP Exclusive ISPs</h3></div>
          {available !== null && (
            <span className={`text-xs font-mono px-2.5 py-1 rounded-full border ${soldOut ? "text-amber-600 border-amber-300" : "text-neon border-neon/30"}`} data-testid="isp-availability-badge">
              {available} of {avail.total} IPs available
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-zinc-500 mb-4">
          <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-neon" aria-hidden="true" /> Ashburn, Virginia</span>
          <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-neon" aria-hidden="true" /> Dedicated static ISPs</span>
        </div>
        <p className="text-zinc-500 text-sm mb-1">$2 / IP <span className="text-ink font-medium">/ month</span> · unlimited bandwidth · same IPs kept on every renewal · minimum 25.</p>
        <p className="text-zinc-400 text-xs mb-5">Billed monthly — cancel anytime.</p>
        {soldOut ? (
          <div className="rounded-xl border border-amber-300 bg-amber-50 text-amber-700 text-sm px-4 py-3" data-testid="isp-soldout-notice">
            Sold out — fewer than 25 IPs are currently available. Please check back soon or contact support.
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <Input type="number" min={25} step={5} max={available ?? undefined} value={qty} onChange={(e) => setQty(Number(e.target.value))} className="w-28 h-11 bg-white border-[#E4EEEC] focus:border-neon rounded-xl font-mono text-ink" data-testid="isp-qty-input" />
              <span className="text-zinc-500 text-sm font-mono" data-testid="isp-price-display">IPs · {coupon
                ? (<><span className="line-through text-zinc-400">${base.toFixed(2)}</span> <span className="text-neon text-lg">${final.toFixed(2)}</span></>)
                : (<span className="text-neon text-lg">${base.toFixed(2)}</span>)}<span className="text-zinc-400">/mo</span></span>
              <Button onClick={buy} className="bg-neon text-white hover:bg-neon-hover font-semibold rounded-xl" data-testid="buy-isp-dashboard-button">Subscribe · {qty} IPs · ${final.toFixed(0)}/mo</Button>
            </div>
            <div className="mt-4"><CouponField product="isp_ip" quantity={qty} onChange={setCoupon} /></div>
          </>
        )}
      </div>
      <div>
        <h3 className="font-head text-lg font-semibold text-ink mb-3">Your ISP subscriptions</h3>
        {ispOrders.length === 0 ? (
          <div className="bg-white border border-[#E4EEEC] rounded-3xl p-10 text-center text-zinc-400">No ISP subscriptions yet.</div>
        ) : (
          <div className="space-y-4">
            {ispOrders.map((o) => (
              <div key={o.order_id} className="bg-white border border-[#E4EEEC] rounded-2xl p-5" data-testid={`isp-order-${o.order_id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-neon text-sm">{o.order_id}</span>
                    <span className="text-zinc-400 text-xs">{o.quantity} ISPs{o.created_at ? ` · ${new Date(o.created_at).toLocaleDateString()}` : ""}</span>
                    <span className={`text-xs font-mono border rounded px-2 py-0.5 ${statusStyle(o.status)}`}>{o.status}</span>
                  </div>
                  {o.status !== "cancelled" && o.proxies?.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => copyProxies(o.proxies)} data-testid={`isp-copy-${o.order_id}`} className="h-8 rounded-lg border-[#CBEEF5] bg-transparent text-ink hover:border-neon hover:text-neon"><Copy className="w-3.5 h-3.5 mr-1" aria-hidden="true" />Copy</Button>
                      <Button size="sm" variant="outline" onClick={() => downloadProxies(o)} data-testid={`isp-download-${o.order_id}`} className="h-8 rounded-lg border-[#CBEEF5] bg-transparent text-ink hover:border-neon hover:text-neon"><Download className="w-3.5 h-3.5 mr-1" aria-hidden="true" />.txt</Button>
                    </div>
                  )}
                </div>
                {o.status === "cancelled" ? (
                  <p className="text-zinc-400 text-sm">Subscription cancelled.</p>
                ) : o.proxies?.length > 0 ? (
                  <pre className="bg-[#061512] text-[#7DF5E4] text-xs font-mono rounded-xl p-4 max-h-56 overflow-auto whitespace-pre" data-testid={`isp-proxies-${o.order_id}`}>{o.proxies.join("\n")}</pre>
                ) : (
                  <p className="text-zinc-400 text-sm">Provisioning… your ISPs will appear here shortly.</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
